import * as React from 'react';

import numeral from 'numeral';
import PropTypes from 'prop-types';
import {
    Bar,
    ComposedChart,
    CartesianGrid,
    Legend,
    Line,
    Tooltip,
    XAxis,
    YAxis,
    ResponsiveContainer,
    // @ts-expect-error
} from 'recharts';
import _ from 'underscore';

import {colors} from 'shells/colors/colors';
import * as ReportColors from 'shells/colors/reports';
import {CloudDownloadIcon} from 'shells/icon';
// @ts-expect-error
import {getInstanceCurrencyPrefix} from 'shells/utils/currency';

import {ChartEmptyState} from '../empty-state/chart-empty-state';
// @ts-expect-error
import {validTickLabelNodeProps} from '../index';

import styles from './hero-chart.module.css';

import sharedStyles from '../../shared.module.css';

const MATCH_LAST_SPACE = /\s(?=\S*$)/;
const SHARED_Y_AXIS_PROPS = {
    tickCount: 7,
    axisLine: false,
    tickLine: false,
} as const;

// Leaving all animations false for now, until we have more XP w/ this library.
const IS_ANIMATION_ACTIVE = false;

const DOMAIN_PADDING = {top: 80, right: 20, bottom: 20, left: 0} as const;
const SVG_WRAPPER_STYLES = {margin: '0 auto'} as const;

// Right position is 5 because of a 10px right margin on the children.
const LEGEND_STYLES = {width: 'auto', top: 22, right: 6, fontSize: 17} as const;

type ODRHeroChartProps = {
    data: Array<{
        name: string;
        y1: number;
        y2: number;
        y1_payload: {
            value: number;
            label?: string;
        };
        y2_payload: {
            value: number;
            label?: string;
        };
    }>;
    exportReportLink?: string;
};

export class ODRHeroChart extends React.PureComponent<ODRHeroChartProps> {
    render() {
        if (this.props.data.length === 0) {
            return <ChartEmptyState isVertical={true} />;
        }
        const currencyPrefix = getInstanceCurrencyPrefix();

        return (
            <div className={styles.container}>
                <ResponsiveContainer height='100%' width='100%'>
                    <ComposedChart
                        data={this.props.data}
                        // Margin is misleading, this is more like padding in
                        // the svg domain.
                        margin={DOMAIN_PADDING}
                        barCategoryGap='15%'
                        style={SVG_WRAPPER_STYLES}
                    >
                        <CartesianGrid stroke={colors.offWhiteMd} vertical={false} />
                        <Tooltip content={renderTooltip} />
                        <Legend iconSize={16} wrapperStyle={LEGEND_STYLES} />
                        <XAxis
                            dataKey='name'
                            tick={this.renderXAxisTickLabel}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            {...SHARED_Y_AXIS_PROPS}
                            yAxisId='right'
                            orientation='right'
                            dataKey='y1'
                            tick={renderYAxisTickLabel({prefix: true, currencyPrefix})}
                        />
                        <YAxis
                            {...SHARED_Y_AXIS_PROPS}
                            yAxisId='left'
                            orientation='left'
                            dataKey='y2'
                            tick={renderYAxisTickLabel({currencyPrefix})}
                        />
                        <Bar
                            isAnimationActive={IS_ANIMATION_ACTIVE}
                            dataKey='y2'
                            name='Lead quantity'
                            yAxisId='left'
                            fill={ReportColors.BASE_BAR_COLOR}
                        />
                        <Line
                            isAnimationActive={IS_ANIMATION_ACTIVE}
                            type='monotone'
                            dataKey='y1'
                            name='Lead value'
                            yAxisId='right'
                            stroke={colors.navy}
                            strokeWidth={2}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
                <ChartActions exportLink={this.props.exportReportLink} />
            </div>
        );
    }

    renderXAxisTickLabel = (props: {
        x: number;
        payload: {
            value: string;
        };
    }) => {
        const lines = props.payload.value.split(MATCH_LAST_SPACE).map((line) => {
            return (
                <tspan key={line} textAnchor='middle' x={props.x} dy='1.1em'>
                    {line}
                </tspan>
            );
        });
        const validNodeProps = _.pick(props, validTickLabelNodeProps);

        return (
            <text {...validNodeProps} fill={colors.grey}>
                {lines}
            </text>
        );
    };
}

// TODO: (Steve) De-dup this code that is also in card-chart.
// I tried to componentize this label (numeral-tick-label.js) rendering
// but was getting a lot of errors in the process with recharts.
// Tabling this for now and will come back.
export const renderYAxisTickLabel = (opts: {prefix?: boolean; currencyPrefix: string}) => {
    const defaultFormat = '0[.]0a';
    const options = {format: defaultFormat, prefix: false, ...opts} as const;

    type YAxisTickLabelProps = {
        payload: {
            value: number;
        };
    };

    return (props: YAxisTickLabelProps) => {
        const value = props.payload.value;
        const numeralFormat = numeral(value).format(options.format);
        const validNodeProps = _.pick(props, validTickLabelNodeProps);

        let result = '';
        if (value !== 0) {
            result = options.prefix ? opts.currencyPrefix + numeralFormat : numeralFormat;
        }

        return (
            <text {...validNodeProps} fill={colors.greyLt} dy='3.5px'>
                {result}
            </text>
        );
    };
};

/**
 * Maps the "subvalues" array of formatted value objects to labeled, formatted strings.
 *
 * @param {Object[]} subvalues Array of subvalue objects
 * @param {string} subvalues.label The text label to display for each subvalue
 * @param {number} subvalues.value The numeric value of this subvalue
 * @param {string} format A string indicating how to format the number
 * @param {string} color Hex string specifying the color of every subvalue
 * @param {string} prefix String prefix, usually the currency symbol from this.props.prefix.
 * @return {Array} Array of JSX children.
 */
const renderTooltipSubvalues = (
    subvalues: Array<{
        label: string;
        value: number;
    }>,
    format: string,
    color: string,
    prefix: string
) => {
    const closingSubvalue = subvalues.find((subvalue) => subvalue.label === 'Closing');
    if (closingSubvalue && closingSubvalue.value === 0) {
        return [];
    }

    return subvalues.map((subvalue) => {
        return (
            <div key={subvalue.label}>
                {subvalue.label}: {prefix}
                {numeral(subvalue.value).format(format)}
            </div>
        );
    });
};

const renderTooltip = (props: {
    active: boolean;
    payload: {
        payload: Array<any>;
    };
    label: string;
}) => {
    if (!props.active) return null;

    // @ts-expect-error
    const item1 = props.payload[0];
    // @ts-expect-error
    const item2 = props.payload[1];
    const item1Payload = item1.payload[`${item1.dataKey}_payload`];
    const item2Payload = item2.payload[`${item2.dataKey}_payload`];
    const format = '0,0[.]0';
    const currencyPrefix = getInstanceCurrencyPrefix();
    const item1Children = renderTooltipSubvalues(item1Payload.subvalues, format, item1.color, '');
    const item2Children = renderTooltipSubvalues(
        item2Payload.subvalues,
        format,
        item2.color,
        currencyPrefix
    );

    return (
        <div className={sharedStyles.tooltip}>
            <div>{props.label}</div>
            <div>
                <strong>{numeral(item1Payload.value).format(format)} leads</strong>
                {item1Children}
            </div>
            <div>
                <strong>
                    {currencyPrefix}
                    {numeral(item2Payload.value).format(format)}
                </strong>
                {item2Children}
            </div>
        </div>
    );
};

ChartActions.propTypes = {
    exportLink: PropTypes.string,
};
// @ts-expect-error
function ChartActions(props) {
    return (
        <div className={styles['chart-actions']}>
            {props.exportLink ? (
                <a
                    className={sharedStyles['download-icon']}
                    href={props.exportLink}
                    data-title='Download CSV'
                    download={true}
                >
                    <CloudDownloadIcon />
                </a>
            ) : undefined}
        </div>
    );
}
